import { decryptFromStore, encryptAndStore, removeFromStore } from './utils/secureStorage'

const BASE = '/api'
const LS_KEY_JWT = 'pwa_jwt'
const LS_KEY_CONFIG = 'pwa_config'

let _on403: ((reason: string) => void) | null = null
export function setOn403(fn: (reason: string) => void) { _on403 = fn }

async function getToken(): Promise<string | null> {
  return await decryptFromStore(LS_KEY_JWT)
}

async function forceLogout() {
  await removeFromStore(LS_KEY_JWT)
  await removeFromStore(LS_KEY_CONFIG)
  window.location.reload()
}

// ── Cichy refresh JWT przez device_token (bez reload) ────────────────────
let _refreshPromise: Promise<boolean> | null = null

async function silentRefreshJWT(): Promise<boolean> {
  // Mutex — jeśli refresh już trwa, czekaj na wynik
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = (async () => {
    try {
      const cfg = await decryptFromStore(LS_KEY_CONFIG)
      if (!cfg) return false
      let parsed: Record<string, unknown> = {}
      try { parsed = JSON.parse(cfg) } catch { return false }
      const deviceToken = parsed.deviceToken as string
      if (!deviceToken) return false

      const r = await fetch(`${BASE}/auth/device-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceToken }),
      })
      if (r.status === 403) {
        try {
          const errData = await r.json()
          if (errData.reason && _on403) _on403(errData.reason)
        } catch { /* ignore */ }
        return false
      }
      if (!r.ok) return false

      const data = await r.json()
      const jwt = data.token
      if (!jwt) return false

      await encryptAndStore(LS_KEY_CONFIG, JSON.stringify({ ...parsed, deviceToken: data.deviceToken, jwt }))
      await encryptAndStore(LS_KEY_JWT, jwt)
      return true
    } catch {
      return false
    } finally {
      _refreshPromise = null
    }
  })()
  return _refreshPromise
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  try {
    const token = await getToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const url = `${BASE}${path}`;
    console.log('WYSYŁAM ZAPYTANIE DO:', url);
    const res = await fetch(url, { ...options, headers })

    // 403 — organizacja/konto/licencja
    if (res.status === 403) {
      const data = await res.json().catch(() => ({}))
      const reason = (data as any).reason || (data as any).message || 'Brak autoryzacji'
      if (_on403) _on403(reason)
      throw new Error(reason)
    }

    // 401 — JWT wygasł, spróbuj cichy refresh i retry
    if (res.status === 401 && token) {
      const refreshed = await silentRefreshJWT()
      if (refreshed) {
        // Retry z nowym tokenem
        const newToken = await getToken()
        const retryHeaders = { ...headers }
        if (newToken) retryHeaders['Authorization'] = `Bearer ${newToken}`
        const retryRes = await fetch(url, { ...options, headers: retryHeaders })
        if (retryRes.status === 403) {
          const retryData = await retryRes.json().catch(() => ({}))
          const reason = (retryData as any).reason || (retryData as any).message || 'Brak autoryzacji'
          if (_on403) _on403(reason)
          throw new Error(reason)
        }
        if (retryRes.ok) {
          const data = await retryRes.json().catch(() => ({}))
          return data as T
        }
      }
      // Refresh nie pomógł — wyloguj
      forceLogout()
      throw new Error('Sesja wygasła — zaloguj się ponownie')
    }

    if (res.status === 401) {
      throw new Error('Sesja wygasła — zaloguj się ponownie')
    }

    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error((data as any).message || `HTTP ${res.status}`)
    return data as T
  } catch (error: any) {
    console.error('BŁĄD ZAPYTANIA:', error?.message);
    throw error;
  }
}


export interface Employee {
  id: number
  first_name: string
  last_name: string
  position: string | null
  short_note?: string
  full_note?: string
}

export interface ConversationItem {
  conversation_id: number
  employee_id: number
  first_name: string
  last_name: string
  position: string | null
  short_summary: string | null
  long_summary: string | null
  arrangements_transcription: string | null
  arrangements_json?: import('./types/arrangement').Arrangement[] | null
  conversation_status?: 'ACCEPTABLE' | 'WARNING' | 'BLOCKED'
  created_at: string
  is_indexing?: boolean
  _optimistic?: boolean
  optimisticId?: string
  _incident?: boolean
  incident_form?: import('./types/incident').IncidentForm
  // Optymistyczny kafelek notatki głosowej nagranej OFFLINE (brak lokalnej
  // transkrypcji). `_voiceHash` = klucz nagrania w Dexie `raw_audio`; gdy
  // `offline_pipeline.syncAudio` wyśle nagranie i skasuje lokalną kopię, Screen1
  // usuwa ten placeholder i pobiera z backendu prawdziwą, przepisaną notatkę.
  _voicePlaceholder?: boolean
  _voiceHash?: string
}

export interface ConversationStarted {
  conversationId: string
  initialTranscription: string
  status: string
}

export interface GeneratedScriptStep {
  step: number
  label: string
  what_to_do: string
  listen_for: string[]
  example_opener: string
}

export interface GeneratedScript {
  conversationModel: 'seven_steps' | 'model_z'
  modelLabel: string
  modelRationale: string
  steps: GeneratedScriptStep[]
}

export interface ScriptStreamStep {
  step: number
  label: string
  phrase: string
}

export interface ScriptStreamMeta {
  model: string
  modelLabel: string
  briefing: string
  complianceWarning?: string
}

export type GuardStatus = 'NOT_BUSINESS' | 'MOBBING_WARNING' | 'PRIVACY_WARNING' | 'OK'

export interface AnalyzeNoteResponse {
  // Legacy pola (kompatybilność)
  detectedName: string
  cleanText: string
  status: string
  warningText: string
  reason?: string  // Powód odrzucenia (np. PERSONAL_ATTACKS, HUMILIATION)
  
  // Nowe pola z zaawansowanej analizy
  correctedName?: string
  contextScore?: number
  conversationStatus?: 'ACCEPTABLE' | 'WARNING' | 'BLOCKED'
  blockReason?: string
}

export interface FinalizedConversation {
  message: string
  fullNote: string
  shortSummary: string
  longSummary: string
  documentContent?: string
  documentType?: string
}

export interface TokenQuota {
  id: number
  userId: number
  organizationId: number
  periodStart: string
  periodEnd: string
  tokensLimit: number
  tokensUsed: number
  tokensRemaining: number
  remainingPct: number
}

export const pwaApi = {
  getMyTokenQuota: () =>
    request<TokenQuota>('/me/token-quota'),

  getEmployees: () =>
    request<{ employees: Employee[] }>('/employees'),

  createConversation: (employeeId: number, initialTranscription: string) =>
    request<ConversationStarted>('/screen1/conversation', {
      method: 'POST',
      body: JSON.stringify({ employeeId, initialTranscription }),
    }),

  getConversation: (conversationId: string | number) =>
    request<{
      conversationId: number;
      fullNote: string;
      shortSummary: string;
      employeeFirstName: string;
      employeeLastName: string;
      createdAt: string;
      status: string;
      arrangements_json?: import('./types/arrangement').Arrangement[] | null;
    }>(
      `/screen1/conversation/${conversationId}`
    ),

  saveNote: (conversationId: string | number, note: string) =>
    request<{ ok: boolean; conversationId: number; shortSummary: string }>(
      `/screen1/conversation/${conversationId}/note`,
      { method: 'POST', body: JSON.stringify({ note }) }
    ),

  updateNote: (conversationId: string | number, note: string) =>
    request<{ ok: boolean }>(
      `/screen1/conversation/${conversationId}/note`,
      { method: 'PATCH', body: JSON.stringify({ note }) }
    ),

  finalizeConversation: (conversationId: string | number, arrangementsTranscription: string, documentType?: string, existingNote?: string, arrangementsJson?: import('./types/arrangement').Arrangement[]) =>
    request<FinalizedConversation>(`/screen3/conversation/${conversationId}/finalize`, {
      method: 'POST',
      body: JSON.stringify({ arrangementsTranscription, ...(documentType ? { documentType } : {}), ...(existingNote ? { existingNote } : {}), ...(arrangementsJson ? { arrangementsJson } : {}) }),
    }),

  patchArrangements: (conversationId: string | number, arrangements: import('./types/arrangement').Arrangement[]) =>
    request<{ arrangements_json: import('./types/arrangement').Arrangement[] }>(`/conversations/${conversationId}/arrangements`, {
      method: 'PATCH',
      body: JSON.stringify({ arrangements }),
    }),

  executeAction: (conversationId: string | number, actionType: 'NOTE' | 'WARNING' | 'REPRIMAND', overrideHrEmail?: string) =>
    request<{ documentId?: number; content?: string; plainText?: string; sentTo?: string | string[] }>(
      `/screen3/conversation/${conversationId}/document`,
      {
        method: 'POST',
        body: JSON.stringify({
          type: actionType.toLowerCase(),
          ...(overrideHrEmail ? { hrEmail: overrideHrEmail } : {}),
        }),
      }
    ),

  analyzeNote: (transcription: string) =>
    request<AnalyzeNoteResponse>('/llm/analyze-note', {
      method: 'POST',
      body: JSON.stringify({ transcription }),
    }),

  sendIncidentReport: (form: import('./types/incident').IncidentForm) =>
    request<{ ok: boolean; sentTo: string }>('/incident/report', {
      method: 'POST',
      body: JSON.stringify(form),
    }),

  createEmployee: (firstName: string, lastName: string, position?: string) =>
    request<{ employee: Employee }>('/employees', {
      method: 'POST',
      body: JSON.stringify({ firstName, lastName, position: position || '' }),
    }),

  updateEmployee: (employeeId: number, data: { firstName?: string; lastName?: string; position?: string }) =>
    request<{ employee: Employee }>(`/employees/${employeeId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteEmployee: (employeeId: number) =>
    request<{ ok: boolean }>(`/employees/${employeeId}`, { method: 'DELETE' }),

  getConversations: () =>
    request<{ items: ConversationItem[] }>('/screen1/list'),

  deleteConversationItem: (conversationId: string | number) =>
    request<{ ok: boolean }>(`/screen1/conversation/${conversationId}`, { method: 'DELETE' }),

  searchConversations: (params: { q?: string; note?: string; dateFrom?: string; dateTo?: string; deadlineVariants?: string; timeFrom?: string; timeTo?: string; keywords?: string }) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([,v]) => v) as [string,string][]).toString()
    return request<{ items: Array<{ conversation_id: number; short_summary: string; long_summary: string; arrangements_transcription: string | null; created_at: string; employee_id: number; first_name: string; last_name: string; position: string | null }>; totalFound: number }>(`/screen1/search?${qs}`)
  },

  parseSearchIntent: (utterance: string) =>
    request<{
      searchMode: 'deadline' | 'conversationDate' | 'mixed' | 'keywords';
      employeeName: string | null;
      conversationDateRange: { from: string; to: string } | null;
      deadlineDateRange: { from: string; to: string } | null;
      deadlineTextVariants: string[] | null;
      timeOfDay: { from: string; to: string } | null;
      keywords: string[] | null;
    }>('/llm/parse-search-intent', {
      method: 'POST',
      body: JSON.stringify({ utterance, now: new Date().toISOString() }),
    }),

  // NOWE: Wyszukiwanie kontekstowe RAG (zawsze przez LLM)
  contextualSearch: (query: string, options?: { within?: number[]; synthesize?: boolean }) =>
    request<{
      items: ConversationItem[];
      ragAnswer: string | null;
      searchMeta: {
        totalFound: number;
        searchTimeMs: number;
        parsedDateStart?: string;
        parsedDateEnd?: string;
        searchConcept?: string;
        fallback?: boolean;
        error?: string;
      };
      warning?: string;
    }>('/screen1/search-rag?' + new URLSearchParams({
      q: query,
      ...(options?.within ? { within: options.within.join(',') } : {}),
      synthesize: options?.synthesize === false ? 'false' : 'true',
    }).toString(), { signal: AbortSignal.timeout(10000) }),

  selectScriptStream: (
    conversationId: string | number,
    callbacks: {
      onMeta:  (meta: ScriptStreamMeta) => void
      onStep:  (step: ScriptStreamStep) => void
      onDone:  (stepCount: number) => void
      onError: (msg: string) => void
      onGuard?: (status: GuardStatus) => void
    }
  ): AbortController => {
    const ac    = new AbortController()

    // FIX: getToken() zwraca Promise - musimy poczekać na wartość
    getToken().then(token => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    fetch(`${BASE}/screen2/conversation/${conversationId}/script/stream`, {
      method: 'POST',
      headers,
      signal: ac.signal,
    }).then(async (res) => {
      if (!res.ok) {
        callbacks.onError(`HTTP ${res.status}`)
        return
      }
      if (!res.body) { callbacks.onError('No response body'); return }

      const reader  = res.body.getReader()
      const decoder = new TextDecoder()
      let lineBuf   = ''

      while (true) {
        let done: boolean, value: Uint8Array | undefined
        try {
          ;({ done, value } = await reader.read())
        } catch (e: any) {
          if (e?.name !== 'AbortError') callbacks.onError(e?.message || 'stream read error')
          break
        }
        if (done) break

        lineBuf += decoder.decode(value, { stream: true })
        const lines = lineBuf.split('\n')
        lineBuf = lines.pop() ?? ''

        let lastEvent = ''
        for (const raw of lines) {
          const line = raw.trimEnd()
          if (line.startsWith('event:')) {
            lastEvent = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            const payload = line.slice(5).trim()
            if (!payload) continue
            try {
              const data = JSON.parse(payload)
              if (lastEvent === 'meta')       callbacks.onMeta(data as ScriptStreamMeta)
              else if (lastEvent === 'step')   callbacks.onStep(data as ScriptStreamStep)
              else if (lastEvent === 'done')   callbacks.onDone(data.stepCount ?? 0)
              else if (lastEvent === 'error')  callbacks.onError(data.message ?? 'stream error')
              else if (lastEvent === 'guard')  callbacks.onGuard?.(data.status as GuardStatus)
            } catch { /* ignore malformed */ }
            lastEvent = ''
          }
        }
      }
    }).catch((e: any) => {
      if (e?.name !== 'AbortError') callbacks.onError(e?.message || 'fetch error')
    })
    }).catch(() => { /* getToken failed */ })

    return ac
  },

  syncOfflineNote: (note: any) =>
    request<{ ok: boolean; conversationId: number; email: string }>('/screen1/offline-sync', {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  // Samouczenie offline: wysyłka zebranych mapowań słownikowych i logu korekt/nierozpoznanych
  // słów, żeby można było je wykorzystać do kuracji słownika zakładowego.
  syncOfflineLearning: (mappings: any[], trainingLog: any[]) =>
    request<{ ok: boolean; received: number }>('/screen1/offline-learning', {
      method: 'POST',
      body: JSON.stringify({ mappings, trainingLog }),
    }),

  // Wysyłka surowego nagrania (audio) z urządzenia na serwer — telefon nie ma
  // gromadzić głosówek w nieskończoność. multipart/form-data, bez JSON-owego
  // Content-Type wymuszanego przez request().
  uploadRawAudio: async (
    hash: string,
    blob: Blob,
    noteId?: string,
    needsTranscription?: boolean,
  ): Promise<{ ok: boolean; transcribed?: boolean; conversationId?: number | null }> => {
    const token = await getToken()
    const form = new FormData()
    form.append('hash', hash)
    if (noteId) form.append('noteId', noteId)
    // Sygnał dla backendu: to nagranie NIE ma lokalnej transkrypcji (dyktowanie
    // offline) — po zapisaniu ma je przepisać na tekst i utworzyć notatkę na E1.
    if (needsTranscription) form.append('needsTranscription', '1')
    form.append('audio', blob, `${hash}.webm`)
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${BASE}/screen1/offline-audio`, { method: 'POST', headers, body: form })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
  },

  // NOWY: Zapis odrzuconej notatki przy blokadzie filtra na E1
  saveRejectedConversation: (employeeId: number, initialTranscription: string, filterStatus: string, filterReason?: string, filterDetails?: string) =>
    request<{ ok: boolean; conversationId: number; message: string }>('/screen1/rejected-conversation', {
      method: 'POST',
      body: JSON.stringify({ employeeId, initialTranscription, filterStatus, filterReason, filterDetails }),
    }),
}
